/**
 * User-scoped preference storage. localStorage today, backend table tomorrow.
 *
 * Keep the public surface (read, write, remove, subscribe) stable so the implementation can
 * swap to a server-side {@code usr_prefs(usr_id, pref_key, pref_value_json)} table later
 * without touching any caller.
 *
 * <p>Keys: every pref is namespaced as {@code ph:<userId>:<key>}. Different users on the same
 * browser don't clobber each other, and the keying maps 1:1 to a future DB row.
 */

const PREFIX = 'ph';

/**
 * Sidebar collapsed/expanded pref. Declared here (not in Sidebar or useLogin) because BOTH
 * write/read it -- slice-3590: a fresh login resets it to collapsed; the Sidebar toggles it.
 */
export const SIDEBAR_COLLAPSED_PREF = 'layout.sidebarCollapsed';

/**
 * Which navigation-tree branches are open (Sidebar). slice-3608 (Chris 2026-09-08 evening,
 * correcting slice-3590): a fresh login resets this to {} — every branch closed, sidebar itself
 * left open — and the active-route effect then opens just the trail to wherever the operator goes.
 */
export const NAV_EXPANDED_PREF = 'nav.expanded.v2';

function fullKey(userId: string, key: string): string {
  return `${PREFIX}:${userId}:${key}`;
}

export function readPref<T>(userId: string, key: string, fallback: T): T {
  if (!userId) return fallback;
  try {
    const raw = window.localStorage.getItem(fullKey(userId, key));
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writePref(userId: string, key: string, value: unknown): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(fullKey(userId, key), JSON.stringify(value));
    // Same-tab listeners — the native 'storage' event only fires in OTHER tabs.
    window.dispatchEvent(
      new CustomEvent('ph-pref-change', { detail: { userId, key } }),
    );
  } catch {
    // Quota exceeded or private mode: silently drop. Prefs are non-critical.
  }
}

export function removePref(userId: string, key: string): void {
  if (!userId) return;
  try {
    window.localStorage.removeItem(fullKey(userId, key));
    window.dispatchEvent(
      new CustomEvent('ph-pref-change', { detail: { userId, key } }),
    );
  } catch {
    // ignore
  }
}

/** Subscribe to changes. Fires for same-tab via custom event + cross-tab via 'storage'. */
export function subscribe(userId: string, key: string, cb: () => void): () => void {
  const full = fullKey(userId, key);

  function onCustom(e: Event) {
    const detail = (e as CustomEvent<{ userId: string; key: string }>).detail;
    if (detail.userId === userId && detail.key === key) cb();
  }
  function onStorage(e: StorageEvent) {
    if (e.key === full) cb();
  }
  window.addEventListener('ph-pref-change', onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('ph-pref-change', onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
